import React, { useState } from 'react';
import {
  Container, Row, Col, Card, Badge, Button, Spinner, Alert,
  Form, InputGroup, Dropdown
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import {
  listPendingToolRequests,
  approveToolRequest,
  rejectToolRequest
} from '../../services/toolRequestService';
import DestructiveConfirmModal from '../../components/common/DestructiveConfirmModal';
import DashboardLayout from '../../components/layout/DashboardLayout';
import KebabMenu from '../../components/common/KebabMenu';
import ViewToggle from '../../components/common/ViewToggle';
import useViewToggle from '../../hooks/useViewToggle';
import './ToolList.css';

/**
 * Bandeja de aprobación de solicitudes de herramienta (L13).
 *
 * POR QUÉ EXISTE
 * --------------
 * Aprobar o rechazar una solicitud vivía SOLO en el carril del agente
 * (`approve_tool_request` / `reject_tool_request`, más un
 * `get_pending_tool_requests_for_supervisor` que ninguna pantalla consumía).
 * En la web no había cola: `/herramientas/mis-solicitudes` (MyToolRequests) es
 * la lista de solicitudes PROPIAS del solicitante, así que el supervisor que
 * clickeaba el aviso caía en su propio empty state.
 *
 * Medido en producción el 2026-07-30: 9.324 avisos `tool_request_created` +
 * 1.521 `tool_request_cancelled` — los dos tipos dirigidos al supervisor —
 * apuntaban a `/herramientas/solicitudes`, una ruta que nunca se construyó. No
 * fue un typo: fue esta pantalla, que faltaba. Es deuda de paridad web↔agente,
 * de las que el CLAUDE.md marca como criterio de aceptación por defecto.
 *
 * CÓMO ESCRIBE
 * ------------
 * Las decisiones NO se escriben acá. Van por `toolRequestService`, que llama a
 * los MISMOS tools MCP que usa el agente (`approve_tool_request` /
 * `reject_tool_request`). O sea que la fila que queda en `tool_requests` es
 * idéntica venga de donde venga — misma transición de estado, mismo
 * `approved_by`, mismo assign inline, mismo audit log, misma notificación al
 * solicitante. No hay una segunda implementación que pueda derivar, que es
 * exactamente la deuda de #353.
 *
 * La LECTURA sí es directa a Supabase (patrón documentado en
 * `toolRequestService`): la policy `tool_requests_select` ya filtra
 * (`requester_id = auth.uid() OR is_supervisor_or_above()`).
 *
 * QUÉ MUESTRA
 * -----------
 * Lo que el supervisor necesita para decidir, no las columnas de la tabla:
 * quién pide, qué herramienta, para qué proyecto, a qué destino, por qué, hace
 * cuánto que espera, y —el dato que ningún volcado de columnas tiene— si al
 * aprobar la herramienta se asigna de inmediato o la solicitud queda en espera
 * (`approve_tool_request` hace el assign inline sólo si `tool.status ===
 * 'available'`).
 *
 * MOBILE-FIRST
 * ------------
 * El supervisor aprueba desde el teléfono, en obra: Aprobar/Rechazar son
 * botones visibles de 44px (no items escondidos en el kebab), la tarjeta apila
 * bajo 640px y no hay nada que dependa de hover.
 */

const REGLA_ANTIGUEDAD_DIAS = 2;

const diasEsperando = (iso) => {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
};

const etiquetaEspera = (iso) => {
  const d = diasEsperando(iso);
  if (d <= 0) return 'hoy';
  return `${d} d esperando`;
};

const fmtFecha = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  });
};

/**
 * Traduce `tools.status` a la consecuencia REAL de aprobar. El estado crudo
 * ("assigned", "maintenance") no le dice nada a quien decide; lo que importa
 * es si la herramienta sale hoy o la solicitud queda colgada.
 */
const consecuenciaDeAprobar = (tool) => {
  if (tool?.status === 'available') {
    return {
      tono: 'success',
      icono: 'bi-check-circle',
      texto: 'Disponible: se asigna de inmediato al aprobar.'
    };
  }
  const motivo = {
    assigned: 'La tiene otro técnico',
    maintenance: 'Está en mantenimiento',
    lost: 'Figura como perdida',
    damaged: 'Figura como dañada',
    retired: 'Está retirada del inventario'
  }[tool?.status] || 'No está disponible';
  return {
    tono: 'warning',
    icono: 'bi-hourglass-split',
    texto: `${motivo}: al aprobar, la solicitud queda en espera y se asigna cuando la devuelvan.`
  };
};

const Avatar = ({ nombre }) => (
  <div
    className="hex-avatar hex-avatar-sm d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
    style={{
      background: '#0057B8', fontSize: '0.75rem', width: 32, height: 32,
      clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)'
    }}
    aria-hidden="true"
  >
    {(nombre || '?').charAt(0).toUpperCase()}
  </div>
);

const ToolRequestsInbox = () => {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { viewMode, setViewMode, isCardView } = useViewToggle();
  const [busqueda, setBusqueda] = useState('');
  const [decision, setDecision] = useState(null); // { solicitud, modo: 'aprobar'|'rechazar' }
  const [errorDecision, setErrorDecision] = useState(null);

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['tool-requests-pending'],
    queryFn: () => listPendingToolRequests(),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true
  });

  // `listPendingToolRequests` devuelve `{ data, error }` en vez de tirar: si no
  // se lee el error de adentro, un "permission denied" se ve idéntico a una
  // bandeja vacía y el supervisor cree que no hay nada que aprobar.
  const errorCarga = error || data?.error || null;
  const solicitudes = errorCarga ? [] : (data?.data || []);

  const filtradas = solicitudes.filter((r) => {
    if (!busqueda.trim()) return true;
    const q = busqueda.trim().toLowerCase();
    return [
      r.requester?.full_name,
      r.tool?.name,
      r.tool?.serial_number,
      r.project?.title,
      r.project?.project_number,
      r.requested_location?.label
    ].some((v) => (v || '').toLowerCase().includes(q));
  });

  const cerrarModal = () => { setDecision(null); setErrorDecision(null); };

  const alTerminar = ({ error: err, message }, exito) => {
    if (err) { setErrorDecision(err.message || 'No se pudo completar la acción.'); return; }
    toast.success(message || exito);
    cerrarModal();
    queryClient.invalidateQueries({ queryKey: ['tool-requests-pending'] });
    queryClient.invalidateQueries({ queryKey: ['my-tool-requests'] });
    queryClient.invalidateQueries({ queryKey: ['active-assignments'] });
    queryClient.invalidateQueries({ queryKey: ['tools-available'] });
  };

  const aprobar = useMutation({
    mutationFn: ({ request_id }) => approveToolRequest({ request_id }),
    onSuccess: (res) => alTerminar(res, 'Solicitud aprobada.'),
    onError: (e) => setErrorDecision(e.message)
  });

  const rechazar = useMutation({
    mutationFn: ({ request_id, reason }) => rejectToolRequest({ request_id, reason }),
    onSuccess: (res) => alTerminar(res, 'Solicitud rechazada.'),
    onError: (e) => setErrorDecision(e.message)
  });

  const abrir = (solicitud, modo) => { setErrorDecision(null); setDecision({ solicitud, modo }); };

  const nombreSolicitante = (r) => r.requester?.full_name || 'Solicitante desconocido';

  // -------------------------------------------------------------------------
  // Piezas compartidas entre tarjeta y tabla
  // -------------------------------------------------------------------------

  const BotonesDecision = ({ r, bloque = false }) => (
    <div className={`d-flex gap-2 ${bloque ? 'flex-column flex-sm-row' : ''}`}>
      <Button
        variant="success"
        className={bloque ? 'flex-fill' : ''}
        style={{ minHeight: 44 }}
        onClick={() => abrir(r, 'aprobar')}
      >
        <i className="bi bi-check-lg me-1" aria-hidden="true" />
        Aprobar
      </Button>
      <Button
        variant="outline-danger"
        className={bloque ? 'flex-fill' : ''}
        style={{ minHeight: 44 }}
        onClick={() => abrir(r, 'rechazar')}
      >
        <i className="bi bi-x-lg me-1" aria-hidden="true" />
        Rechazar
      </Button>
    </div>
  );

  const Kebab = ({ r }) => (
    <KebabMenu
      label={`Acciones de la solicitud de ${nombreSolicitante(r)}`}
      stopPropagation={false}
      toggleStyle={{ minHeight: 44, minWidth: 44 }}
    >
      {/*
        "Ver herramienta" SOLO para admin, y no es un descuido: `ToolDetail.js`
        corta en la linea 116 con `if (!isAdmin) return <Alert>Acceso
        restringido a administradores</Alert>`, contradiciendo tanto a su propio
        RoleGuard (admin+supervisor+technician en App.js) como al sidebar.
        `ToolList.js:140` hace lo mismo. Ofrecerle ese link al supervisor seria
        reproducir dentro de esta pantalla exactamente el falso permiso denegado
        que este lote vino a eliminar: mejor no ofrecer la puerta que ofrecer una
        que rebota. Cuando alguien arregle ToolDetail (no tiene dueño asignado),
        se borra esta condicion y listo.
      */}
      {isAdmin && (
        <Dropdown.Item as={Link} to={`/herramientas/${r.tool?.id}`}>
          <i className="bi bi-box-seam me-2" aria-hidden="true" />Ver herramienta
        </Dropdown.Item>
      )}
      {r.project?.id && (
        <Dropdown.Item as={Link} to={`/proyectos/${r.project.id}`}>
          <i className="bi bi-briefcase me-2" aria-hidden="true" />Ver proyecto
        </Dropdown.Item>
      )}
      <Dropdown.Divider />
      <Dropdown.Item onClick={() => abrir(r, 'aprobar')}>
        <i className="bi bi-check-lg me-2 text-success" aria-hidden="true" />Aprobar solicitud
      </Dropdown.Item>
      <Dropdown.Item className="text-danger" onClick={() => abrir(r, 'rechazar')}>
        <i className="bi bi-x-lg me-2" aria-hidden="true" />Rechazar solicitud
      </Dropdown.Item>
    </KebabMenu>
  );

  const Espera = ({ r }) => {
    const d = diasEsperando(r.created_at);
    const alerta = d >= REGLA_ANTIGUEDAD_DIAS;
    return (
      <span
        className={alerta ? 'text-danger fw-bold' : 'text-muted'}
        title={`Enviada el ${fmtFecha(r.created_at)}`}
      >
        <i className="bi bi-clock-history me-1" aria-hidden="true" />
        {etiquetaEspera(r.created_at)}
      </span>
    );
  };

  // -------------------------------------------------------------------------

  const renderTarjeta = (r) => {
    const consecuencia = consecuenciaDeAprobar(r.tool);
    return (
      <Col key={r.id} xs={12} md={6} xxl={4} className="mb-3">
        <Card className="h-100 modern-card">
          <Card.Body className="p-3">
            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
              <div className="d-flex gap-2 align-items-center" style={{ minWidth: 0 }}>
                <Avatar nombre={r.requester?.full_name} />
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold text-truncate">{nombreSolicitante(r)}</div>
                  <div className="small text-muted">solicita una herramienta</div>
                </div>
              </div>
              <Kebab r={r} />
            </div>

            <div className="d-flex gap-2 align-items-center mb-2">
              {r.tool?.image_url ? (
                <img
                  src={r.tool.image_url}
                  alt=""
                  style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flexShrink: 0, background: '#f8f9fa' }}
                />
              ) : (
                <div
                  style={{
                    width: 48, height: 48, background: '#f8f9fa', borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                  }}
                >
                  <i className="bi bi-tools text-muted" aria-hidden="true" />
                </div>
              )}
              <div style={{ minWidth: 0 }}>
                <div className="fw-semibold text-truncate">{r.tool?.name || '—'}</div>
                <code className="small text-muted">{r.tool?.serial_number || 's/n'}</code>
              </div>
            </div>

            <div className="small">
              {r.project && (
                <div className="text-muted">
                  <i className="bi bi-briefcase me-1" aria-hidden="true" />
                  {r.project.project_number ? `${r.project.project_number} — ` : ''}{r.project.title}
                </div>
              )}
              {r.requested_location && (
                <div className="text-muted">
                  <i className="bi bi-geo-alt me-1" aria-hidden="true" />
                  {r.requested_location.label}
                </div>
              )}
              {r.reason && (
                <div className="mt-1">
                  <i className="bi bi-chat-left-text text-muted me-1" aria-hidden="true" />
                  {r.reason}
                </div>
              )}
              <div className="mt-1"><Espera r={r} /></div>
            </div>

            <Alert variant={consecuencia.tono} className="mt-2 mb-3 py-2 small">
              <i className={`bi ${consecuencia.icono} me-1`} aria-hidden="true" />
              {consecuencia.texto}
            </Alert>

            <BotonesDecision r={r} bloque />
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderFila = (r) => {
    const consecuencia = consecuenciaDeAprobar(r.tool);
    return (
      <tr key={r.id}>
        <td>
          <div className="d-flex align-items-center gap-2">
            <Avatar nombre={r.requester?.full_name} />
            <span>{nombreSolicitante(r)}</span>
          </div>
        </td>
        <td>
          <div className="fw-semibold">{r.tool?.name || '—'}</div>
          <code className="small text-muted">{r.tool?.serial_number || 's/n'}</code>
        </td>
        <td>
          {r.project && (
            <div>{r.project.project_number ? `${r.project.project_number} — ` : ''}{r.project.title}</div>
          )}
          {r.requested_location && (
            <div className="small text-muted">
              <i className="bi bi-geo-alt me-1" aria-hidden="true" />{r.requested_location.label}
            </div>
          )}
          {!r.project && !r.requested_location && <span className="text-muted">—</span>}
        </td>
        <td>{r.reason || <span className="text-muted">—</span>}</td>
        <td><Espera r={r} /></td>
        <td>
          <Badge bg={consecuencia.tono === 'success' ? 'success' : 'warning'} text={consecuencia.tono === 'success' ? undefined : 'dark'}>
            <i className={`bi ${consecuencia.icono} me-1`} aria-hidden="true" />
            {consecuencia.texto}
          </Badge>
        </td>
        <td>
          <div className="d-flex align-items-center gap-2 justify-content-end">
            <BotonesDecision r={r} />
            <Kebab r={r} />
          </div>
        </td>
      </tr>
    );
  };

  const sol = decision?.solicitud;
  const esAprobar = decision?.modo === 'aprobar';
  const consecuenciaModal = sol ? consecuenciaDeAprobar(sol.tool) : null;

  return (
    <DashboardLayout>
      <Container fluid className="py-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
          <h2 className="modern-title mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-clipboard-check" aria-hidden="true" />
            Solicitudes por aprobar
          </h2>
          <div className="d-flex gap-2 align-items-center">
            <Badge bg={filtradas.length > 0 ? 'warning' : 'secondary'} text={filtradas.length > 0 ? 'dark' : undefined} className="fs-6">
              {filtradas.length} pendiente{filtradas.length === 1 ? '' : 's'}
            </Badge>
            <Button
              variant="outline-secondary"
              onClick={() => refetch()}
              disabled={isFetching}
              style={{ minHeight: 44 }}
              aria-label="Refrescar la lista"
            >
              <i className="bi bi-arrow-clockwise" aria-hidden="true" />
            </Button>
            <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          </div>
        </div>

        {solicitudes.length > 0 && (
          <Row className="g-2 mb-3">
            <Col xs={12} md={6} lg={4}>
              <InputGroup>
                <InputGroup.Text><i className="bi bi-search" aria-hidden="true" /></InputGroup.Text>
                <Form.Control
                  placeholder="Buscar por técnico, herramienta o proyecto..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  style={{ minHeight: 44 }}
                  aria-label="Buscar solicitudes"
                />
              </InputGroup>
            </Col>
          </Row>
        )}

        {isLoading && (
          <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
        )}

        {errorCarga && (
          <Alert variant="danger">
            <strong>No se pudieron cargar las solicitudes.</strong>
            <div className="mt-1">{errorCarga.message}</div>
          </Alert>
        )}

        {!isLoading && !errorCarga && solicitudes.length === 0 && (
          <Alert variant="info" className="text-center py-4">
            <i className="bi bi-inbox fs-1 d-block mb-2 text-muted" aria-hidden="true" />
            <h5>Sin solicitudes pendientes.</h5>
            <p className="mb-0 text-muted">
              Cuando alguien solicite una herramienta que requiere aprobación,
              te llega una notificación y la solicitud aparece acá.
            </p>
          </Alert>
        )}

        {!isLoading && !errorCarga && solicitudes.length > 0 && filtradas.length === 0 && (
          <Alert variant="secondary">Ninguna solicitud coincide con «{busqueda}».</Alert>
        )}

        {!isLoading && !errorCarga && filtradas.length > 0 && (
          isCardView ? (
            <Row>{filtradas.map(renderTarjeta)}</Row>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Solicitante</th>
                    <th>Herramienta</th>
                    <th>Destino</th>
                    <th>Motivo</th>
                    <th>Espera</th>
                    <th>Al aprobar</th>
                    <th className="text-end">Decisión</th>
                  </tr>
                </thead>
                <tbody>{filtradas.map(renderFila)}</tbody>
              </table>
            </div>
          )
        )}
      </Container>

      <DestructiveConfirmModal
        show={!!decision}
        onHide={cerrarModal}
        title={esAprobar ? 'Aprobar solicitud' : 'Rechazar solicitud'}
        icon={esAprobar ? 'bi-check-circle' : 'bi-x-octagon'}
        variant={esAprobar ? 'success' : 'danger'}
        description={sol ? (
          <>
            <strong>{nombreSolicitante(sol)}</strong> pidió{' '}
            <strong>{sol.tool?.name}</strong>
            {sol.tool?.serial_number && <> <code>({sol.tool.serial_number})</code></>}
            {sol.requested_location && <> para <strong>{sol.requested_location.label}</strong></>}
            .
          </>
        ) : null}
        consequences={esAprobar
          ? [
            consecuenciaModal?.texto || '',
            'Queda registrado que la aprobaste vos.',
            'El solicitante recibe la notificación al instante.'
          ].filter(Boolean)
          : [
            'La solicitud queda cerrada como rechazada y no se puede reabrir.',
            'El solicitante ve el motivo que escribas.',
            'La herramienta no se mueve de donde está.'
          ]}
        reasonRequired={!esAprobar}
        reasonLabel="Motivo del rechazo"
        reasonPlaceholder="Ej: la necesita el equipo de la planta hasta el viernes"
        submitLabel={esAprobar ? 'Confirmar aprobación' : 'Confirmar rechazo'}
        pending={aprobar.isPending || rechazar.isPending}
        error={errorDecision}
        onConfirm={({ reason }) => {
          if (!sol) return;
          if (esAprobar) aprobar.mutate({ request_id: sol.id });
          else rechazar.mutate({ request_id: sol.id, reason });
        }}
      />
    </DashboardLayout>
  );
};

export default ToolRequestsInbox;
